// Generated by TaroBase SDK Generator: 2026-10-04T15:00:51.103Z
// Collection: donations/$donationId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Peer-to-peer token donations. Each record represents a single donation from one user to another. The onchain hook transfers the specified token directly from donor to recipient. Supports SOL, BONK, SKR, MEW, USDC, ANGEL (AngelicSol), GMT, and DRAGON tokens.
 */

export interface DonationsRequest {
  donor: AddressType;
  recipient: AddressType;
  tokenMint: string;
  amount: number | TimeOperation | IncrementOperation | TokenAmount;
  batchId: string;
  ts: number | TimeOperation | IncrementOperation | TokenAmount;
  recipientLockId: string;
}

export interface DonationsResponse {
  donor: string;
  recipient: string;
  tokenMint: string;
  amount: number;
  batchId: string;
  ts: number;
  recipientLockId: string;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a Donations operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDonations(donationId: string, data?: DonationsRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `donations/${donationId}`, document: doc };
}

/**
 * Authenticated users create a donation by specifying donor (must be their wallet), recipient, tokenMint (SOL, BONK, SKR, MEW, USDC, ANGEL/AngelicSol, GMT, or DRAGON), amount in base units, batchId (to group batch donations), ts, and recipientLockId (the FULL document ID of an active lock belonging to the recipient - frontend must look up one of the recipient's active locks and pass its ID). SECURITY: ts MUST be server time — the rule enforces @newData.ts within +/-300 seconds of @time.now, so a client cannot backdate or forward-date a donation. Clients MUST send Time.Now for ts (never a client clock value); any other value outside the 5-minute window is rejected. This prevents shifting a donation into a different monthly raffle period or past the leaderboard epoch cutoff. The rule verifies the referenced lock exists, belongs to the recipient, and has status 'active', ensuring donations only go to committed users who are actively locking tokens. The onchain hook transfers the specified amount directly from the donor to the recipient via @TokenPlugin.transfer. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setDonations(donationId: string, data?: DonationsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`donations/${donationId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Donations: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can view all donation records to see donation history and leaderboards.
   (Get Single Item)
 */
export async function getDonations(donationId: string): Promise<DonationsResponse | null> {
  try {
    const result = await get(`donations/${donationId}`);
    return result as DonationsResponse | null;
  } catch (error) {
    console.error(`Error getting Donations: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Donations document. (
  Read Operation Details: Anyone can view all donation records to see donation history and leaderboards.
  )
 */
export function subscribeDonations(callback: (data: DonationsResponse | null) => void, donationId: string): Promise<() => Promise<void>> {
  return subscribe(`donations/${donationId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as DonationsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Donations at path '${`donations/${donationId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Donations items from collection donations
 
  Read Operation Details: Anyone can view all donation records to see donation history and leaderboards.
  
 */
export async function getManyDonations(filter: string = ""): Promise<DonationsResponse[]> {
  try {
    const results = await get("donations", { prompt: filter });
    return Array.isArray(results) ? results as DonationsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Donations collection at path 'donations': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Donations collection at donations
 
  Read Operation Details: Anyone can view all donation records to see donation history and leaderboards.
  
 */
export function subscribeManyDonations(callback: (data: DonationsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("donations", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as DonationsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Donations collection at path '${"donations"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Donations items from collection donations
 
  Read Operation Details: Anyone can view all donation records to see donation history and leaderboards.
  
 */
export async function getAllDonations(filter: string = ""): Promise<DonationsResponse[]> {
  try {
    const results = await get("donations", { prompt: filter });
    return Array.isArray(results) ? results as DonationsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Donations collection at path 'donations': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Donations collection at donations
 
  Read Operation Details: Anyone can view all donation records to see donation history and leaderboards.
  
 */
export function subscribeAllDonations(callback: (data: DonationsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("donations", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as DonationsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Donations collection at path '${"donations"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Donations items in collection donations.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countDonations(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("donations", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Donations at path 'donations': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Donations items in collection donations.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateDonations(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("donations", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Donations at path 'donations': ${formatError(error)}`);
    return { value: 0 };
  }
}

